import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { Search, MapPin, Phone, Mail, Globe, Video, X, Users } from "lucide-react";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    const pts = points.filter((p) => p.latitude != null && p.longitude != null);
    if (pts.length) {
      map.fitBounds(pts.map((p) => [p.latitude, p.longitude]), { padding: [40, 40], maxZoom: 9 });
    }
  }, [points, map]);
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target?.latitude != null) {
      map.flyTo([target.latitude, target.longitude], 13, { duration: 0.8 });
    }
  }, [target, map]);
  return null;
}

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export default function Annuaire() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [specialite, setSpecialite] = useState("");
  const [public_, setPublic_] = useState("");
  const [langue, setLangue] = useState("");
  const [teleconsultation, setTeleconsultation] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (qDebounced) params.set("q", qDebounced);
    if (specialite) params.set("specialite", specialite);
    if (public_) params.set("public", public_);
    if (langue) params.set("langue", langue);
    if (teleconsultation) params.set("teleconsultation", "true");
    api(`/annuaire?${params}`).then((d) => {
      setData(d);
      setSelected((sel) => (sel && !d.praticiens.some((p) => p.person_id === sel.person_id) ? null : sel));
    }).catch((e) => setError(e.message));
  }, [qDebounced, specialite, public_, langue, teleconsultation]);

  const praticiens = data?.praticiens || [];
  const filtres = data?.filtres || { specialites: [], publics: [], langues: [] };
  const surCarte = praticiens.filter((p) => p.latitude != null && p.longitude != null);

  return (
    <AppLayout>
      <div data-testid="annuaire-page" className="space-y-5 animate-fade-in">
        <div>
          <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Annuaire des praticiens</h1>
          <p className="text-sm text-cfhtb-text mt-1">
            {data ? `${praticiens.length} praticien(s) référencé(s)` : "Chargement…"}
          </p>
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-4 flex flex-wrap items-center gap-3" data-testid="annuaire-filtres">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 text-cfhtb-light absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="annuaire-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nom, cabinet, ville, code postal…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-slate-200 focus:border-cfhtb-primary focus:outline-none focus:ring-1 focus:ring-cfhtb-primary"
            />
          </div>
          <select data-testid="annuaire-filtre-specialite" value={specialite} onChange={(e) => setSpecialite(e.target.value)}
            className="text-sm rounded-md border border-slate-200 px-3 py-2 focus:border-cfhtb-primary focus:outline-none">
            <option value="">Toutes spécialités</option>
            {filtres.specialites.map((s) => <option key={s} value={s}>{cap(s)}</option>)}
          </select>
          <select data-testid="annuaire-filtre-public" value={public_} onChange={(e) => setPublic_(e.target.value)}
            className="text-sm rounded-md border border-slate-200 px-3 py-2 focus:border-cfhtb-primary focus:outline-none">
            <option value="">Tous publics</option>
            {filtres.publics.map((s) => <option key={s} value={s}>{cap(s)}</option>)}
          </select>
          <select data-testid="annuaire-filtre-langue" value={langue} onChange={(e) => setLangue(e.target.value)}
            className="text-sm rounded-md border border-slate-200 px-3 py-2 focus:border-cfhtb-primary focus:outline-none">
            <option value="">Toutes langues</option>
            {filtres.langues.map((s) => <option key={s} value={s}>{cap(s)}</option>)}
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-cfhtb-heading cursor-pointer">
            <input
              type="checkbox"
              data-testid="annuaire-filtre-teleconsultation"
              checked={teleconsultation}
              onChange={(e) => setTeleconsultation(e.target.checked)}
              className="rounded border-slate-300 text-cfhtb-primary focus:ring-cfhtb-primary"
            />
            Téléconsultation
          </label>
        </div>

        {error ? (
          <div className="bg-white rounded-lg border border-cfhtb-error/30 p-6 text-sm text-cfhtb-error" data-testid="annuaire-error">{error}</div>
        ) : !data ? (
          <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
        ) : (
          <div className="grid gap-5 lg:grid-cols-3">
            <div className="lg:col-span-2 relative z-0 rounded-lg overflow-hidden border border-cfhtb-light/30 shadow-sm" data-testid="annuaire-carte">
              <MapContainer center={[46.8, 2.5]} zoom={5} className="h-[420px] lg:h-[620px] w-full" scrollWheelZoom>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds points={surCarte} />
                <FlyTo target={selected} />
                <MarkerClusterGroup chunkedLoading>
                  {surCarte.map((p) => (
                    <Marker
                      key={p.person_id}
                      position={[p.latitude, p.longitude]}
                      eventHandlers={{ click: () => setSelected(p) }}
                    />
                  ))}
                </MarkerClusterGroup>
              </MapContainer>
            </div>

            <div className="space-y-3 lg:max-h-[620px] lg:overflow-y-auto pr-1" data-testid="annuaire-liste">
              {selected && (
                <div className="bg-white rounded-lg border-2 border-cfhtb-primary shadow-md p-5" data-testid="annuaire-fiche">
                  {selected.photo_url && (
                    <img src={selected.photo_url} alt="" data-testid="fiche-photo"
                      className="h-20 w-20 rounded-full object-cover border border-cfhtb-light/30 mb-3" />
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-serif text-lg font-semibold text-cfhtb-heading" data-testid="fiche-nom">
                        {selected.civilite ? `${selected.civilite} ` : ""}{selected.nom_complet}
                      </p>
                      {selected.cabinet_nom && <p className="text-sm text-cfhtb-secondary font-medium">{selected.cabinet_nom}</p>}
                    </div>
                    <button data-testid="fiche-fermer" onClick={() => setSelected(null)}
                      className="text-cfhtb-text hover:text-cfhtb-heading transition-colors" aria-label="Fermer la fiche">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-sm text-cfhtb-text mt-2 inline-flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-cfhtb-light" />
                    {selected.code_postal} {selected.ville}{selected.pays && selected.pays !== "France" ? ` (${selected.pays})` : ""}
                  </p>
                  {selected.specialites.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {selected.specialites.map((s) => (
                        <span key={s} className="text-xs bg-cfhtb-primary/10 text-cfhtb-primary px-2 py-0.5 rounded-full font-medium">{cap(s)}</span>
                      ))}
                    </div>
                  )}
                  {selected.teleconsultation && (
                    <p className="text-xs text-cfhtb-success font-medium mt-2 inline-flex items-center gap-1">
                      <Video className="h-3.5 w-3.5" /> Téléconsultation possible
                    </p>
                  )}
                  {selected.presentation && (
                    <p className="text-sm text-cfhtb-text mt-3 leading-relaxed">{selected.presentation}</p>
                  )}
                  <div className="mt-4 space-y-1.5 text-sm">
                    {selected.telephone_public && (
                      <a href={`tel:${selected.telephone_public}`} data-testid="fiche-tel"
                        className="flex items-center gap-2 text-cfhtb-heading hover:text-cfhtb-primary transition-colors">
                        <Phone className="h-3.5 w-3.5 text-cfhtb-light" /> {selected.telephone_public}
                      </a>
                    )}
                    {selected.email_public && (
                      <a href={`mailto:${selected.email_public}`} data-testid="fiche-email"
                        className="flex items-center gap-2 text-cfhtb-heading hover:text-cfhtb-primary transition-colors">
                        <Mail className="h-3.5 w-3.5 text-cfhtb-light" /> {selected.email_public}
                      </a>
                    )}
                    {selected.site_web && (
                      <a href={selected.site_web} target="_blank" rel="noreferrer" data-testid="fiche-site"
                        className="flex items-center gap-2 text-cfhtb-heading hover:text-cfhtb-primary transition-colors">
                        <Globe className="h-3.5 w-3.5 text-cfhtb-light" /> Site web
                      </a>
                    )}
                  </div>
                </div>
              )}

              {praticiens.length === 0 ? (
                <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 text-sm text-cfhtb-text" data-testid="annuaire-vide">
                  Aucun praticien ne correspond à ces critères.
                </div>
              ) : (
                praticiens.map((p) => (
                  <button
                    key={p.person_id}
                    data-testid={`annuaire-item-${p.person_id}`}
                    onClick={() => setSelected(p)}
                    className={`w-full text-left bg-white rounded-lg border shadow-sm p-4 transition-all hover:shadow-md ${
                      selected?.person_id === p.person_id ? "border-cfhtb-primary" : "border-cfhtb-light/30 hover:border-cfhtb-light"
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      {p.photo_url && (
                        <img src={p.photo_url} alt="" className="h-9 w-9 rounded-full object-cover shrink-0" loading="lazy" />
                      )}
                      <p className="font-medium text-sm text-cfhtb-heading">{p.civilite ? `${p.civilite} ` : ""}{p.nom_complet}</p>
                    </span>
                    <p className="text-xs text-cfhtb-text mt-0.5 inline-flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {p.code_postal} {p.ville}
                      {p.latitude == null && <span className="text-cfhtb-light">(hors carte)</span>}
                    </p>
                    {p.specialites.length > 0 && (
                      <p className="text-xs text-cfhtb-secondary mt-1.5">{p.specialites.map(cap).join(" · ")}</p>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
